import { ArrowRight, MapPin } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ResponsiveImage } from '../components/ResponsiveImage'
import { TransformationCompare } from '../components/TransformationCompare'
import { images } from '../data/images'
import { contact, teamMembers } from '../data/site'
import { transformations } from '../data/transformations'

const featuredTeam = teamMembers.filter((member) => member.image).slice(0, 3)

const featuredServices = [
  { number: '01', title: 'Cut & Style', description: 'Shape, movement, and finishing designed for your face, texture, and routine.', view: 'salon', image: 'services-salon' as const, position: 'center 55%' },
  { number: '02', title: 'Color', description: 'Dimensional, nuanced color grounded in a thorough consultation.', view: 'salon', image: 'gallery-08' as const, position: 'center 58%' },
  { number: '03', title: 'Blonding', description: 'Tailored brightness with intentional placement and healthy-looking shine.', view: 'salon', image: 'gallery-02' as const, position: '48% center' },
  { number: '04', title: 'Extensions', description: 'A personalized approach to added length, density, and seamless movement.', view: 'salon', image: 'gallery-05' as const, position: '44% center' },
  { number: '05', title: 'Skin & Spa', description: 'Restorative treatments for skin, body, brows, and lashes in a private setting.', view: 'spa', image: 'services-spa' as const, position: 'center 56%' },
]

export function Home() {
  const [activeService, setActiveService] = useState(0)
  const [serviceImagesReady, setServiceImagesReady] = useState(false)
  const servicesRef = useRef<HTMLElement>(null)
  const service = featuredServices[activeService]

  const activateService = useCallback((index: number) => {
    setActiveService((current) => current === index ? current : index)
  }, [])

  useEffect(() => {
    const section = servicesRef.current
    if (!section || serviceImagesReady) return
    if (!('IntersectionObserver' in window)) {
      setServiceImagesReady(true)
      return
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      setServiceImagesReady(true)
      observer.disconnect()
    }, { rootMargin: '600px 0px' })
    observer.observe(section)
    return () => observer.disconnect()
  }, [serviceImagesReady])

  return (
    <>
      <section className="campaign-hero">
        <div className="campaign-hero__image" aria-hidden="true">
          <ResponsiveImage image={images['gallery-01']} alt="" sizes="100vw" objectPosition="50% center" priority />
        </div>
        <div className="campaign-hero__shade" aria-hidden="true" />
        <div className="shell campaign-hero__content">
          <p className="campaign-hero__location">San Juan Capistrano <span /> California</p>
          <div className="campaign-hero__title reveal is-visible">
            <span>Diamond Salon &amp; Spa</span>
            <h1>Beauty,<br /><em>refined.</em></h1>
          </div>
          <div className="campaign-hero__intro reveal is-visible">
            <p>Personal hair and spa services, shaped with expertise and delivered with quiet confidence.</p>
            <div className="button-row">
              <a className="button button--light" href={contact.bookingUrl} target="_blank" rel="noopener noreferrer">Book an Appointment <ArrowRight aria-hidden="true" /></a>
              <Link className="text-link text-link--light" to="/services">Explore Services <ArrowRight /></Link>
            </div>
          </div>
          <p className="campaign-hero__edition">Hair / Skin / Care <b>— 2026</b></p>
        </div>
      </section>

      <section className="transformation-section section reveal reveal--orchestrated" aria-labelledby="transformation-title">
        <div className="shell transformation-section__intro">
          <div>
            <span className="editorial-index">01 / The work</span>
            <div className="editorial-heading-mask"><h2 id="transformation-title">The<br /><em>transformation.</em></h2></div>
          </div>
          <div className="transformation-section__lead">
            <p>A first look at the craft behind the chair. Explore real client transformations through a seamless before-and-after reveal on every screen.</p>
            <span>Drag the line to compare</span>
          </div>
        </div>
        <div className="shell transformation-list">
          {transformations.map((item) => <TransformationCompare item={item} key={item.number} />)}
        </div>
      </section>

      <section className="diamond-statement section">
        <div className="shell diamond-statement__grid reveal reveal--heading">
          <span className="editorial-index editorial-index--light">02 / The Diamond experience</span>
          <h2>Modern craft.<br />Personal attention.<br /><em>Entirely you.</em></h2>
          <div>
            <p>Diamond brings hair artists and spa professionals together under one roof. Every visit begins with listening, then becomes a service designed for your features, routine, and goals.</p>
            <Link className="text-link text-link--light" to="/our-space">Step inside the salon <ArrowRight /></Link>
          </div>
        </div>
      </section>

      <section ref={servicesRef} className="editorial-services section reveal reveal--orchestrated" aria-labelledby="services-title">
        <div className="shell editorial-services__head">
          <span className="editorial-index">03 / Services</span>
          <div className="editorial-heading-mask"><h2 id="services-title">Your look.<br /><em>Our craft.</em></h2></div>
          <p>Considered services. Thoughtful consultations. Results made to feel effortless.</p>
        </div>
        <div className="shell editorial-services__layout">
          <div className="editorial-services__list" role="group" aria-label="Featured services">
            {featuredServices.map((item, index) => (
              <button
                className={index === activeService ? 'is-active' : ''}
                type="button"
                onClick={() => activateService(index)}
                onMouseEnter={() => activateService(index)}
                aria-pressed={index === activeService}
                key={item.number}
              >
                <span>{item.number}</span>
                <strong>{item.title}</strong>
                <ArrowRight />
              </button>
            ))}
          </div>
          <div className="editorial-services__feature">
            <div className="editorial-services__media" aria-hidden="true">
              {featuredServices.map((item, index) => (
                <ResponsiveImage
                  className={index === activeService ? 'is-active' : ''}
                  image={images[item.image]}
                  alt=""
                  sizes="(max-width: 860px) 100vw, 44vw"
                  objectPosition={item.position}
                  priority={serviceImagesReady}
                  key={item.number}
                />
              ))}
            </div>
            <div className="editorial-services__feature-copy">
              <div className="editorial-services__descriptions" aria-live="polite" aria-atomic="true">
                {featuredServices.map((item, index) => (
                  <p className={index === activeService ? 'is-active' : ''} aria-hidden={index !== activeService} key={item.number}>{item.description}</p>
                ))}
              </div>
              <Link className="text-link text-link--light" to={`/services?view=${service.view}`}>View service menu <ArrowRight /></Link>
            </div>
          </div>
        </div>
        <div className="shell editorial-services__all">
          <Link className="text-link" to="/services">Explore all services <ArrowRight /></Link>
        </div>
      </section>

      <section className="salon-story section">
        <div className="shell salon-story__grid">
          <div className="salon-story__title reveal reveal--heading">
            <span className="editorial-index">04 / The salon</span>
            <h2>A modern approach<br />to <em>beauty.</em></h2>
          </div>
          <div className="salon-story__image salon-story__image--large reveal reveal--image">
            <ResponsiveImage image={images['gallery-08']} alt="Emerald guest chairs in a sunlit Diamond Salon corner" sizes="(max-width: 860px) 100vw, 50vw" objectPosition="center 58%" />
          </div>
          <div className="salon-story__image salon-story__image--small reveal reveal--image">
            <ResponsiveImage image={images['gallery-07']} alt="Salon mirror and floral wall detail" sizes="(max-width: 620px) 42vw, 26vw" objectPosition="46% center" />
          </div>
          <div className="salon-story__copy reveal">
            <p>A bright, polished retreat where expert technique meets genuine hospitality. Come for the craft. Stay for the easy pace, warm conversation, and the feeling that nothing about your appointment was rushed.</p>
            <Link className="text-link" to="/our-space">Explore our space <ArrowRight /></Link>
          </div>
        </div>
      </section>

      <section className="team-preview section shell">
        <div className="team-preview__heading reveal reveal--heading">
          <div>
            <span className="editorial-index">05 / The artists</span>
            <h2>Experience<br />meets <em>artistry.</em></h2>
          </div>
          <Link className="text-link" to="/team">Meet the full team <ArrowRight /></Link>
        </div>
        <div className="team-preview__grid">
          {featuredTeam.map((member, index) => (
            <article className="team-preview__person reveal" key={member.name}>
              <ResponsiveImage image={member.image!} alt={member.name} sizes="(max-width: 620px) 50vw, 30vw" objectPosition="center 20%" />
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{member.name}</h3>
              <p>{member.role}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="professional-callout shell reveal reveal--heading">
        <span className="editorial-index editorial-index--light">For beauty professionals</span>
        <h2>Make your next move<br /><em>at Diamond.</em></h2>
        <div>
          <p>Looking for a polished space to grow your independent beauty practice?</p>
          <Link className="text-link text-link--light" to="/join-our-team">Join the collective <ArrowRight /></Link>
        </div>
      </section>

      <section className="home-gallery section" aria-labelledby="home-gallery-title">
        <div className="shell home-gallery__heading reveal reveal--heading">
          <span className="editorial-index editorial-index--light">06 / A closer look</span>
          <h2 id="home-gallery-title">Inside<br /><em>Diamond.</em></h2>
          <Link className="text-link text-link--light" to="/our-space">View the gallery <ArrowRight /></Link>
        </div>
        <div className="home-gallery__strip reveal reveal--image">
          <ResponsiveImage image={images['gallery-03']} alt="Private Diamond Salon spa room" sizes="(max-width: 860px) 40vw, 23vw" objectPosition="46% 58%" />
          <ResponsiveImage image={images['gallery-01']} alt="Diamond Salon reception and guest seating" sizes="(max-width: 620px) 100vw, 52vw" objectPosition="45% center" />
          <ResponsiveImage image={images['gallery-09']} alt="Diamond Salon coffee bar" sizes="24vw" objectPosition="center 54%" />
        </div>
      </section>

      <section className="visit-section section shell">
        <div className="visit-section__image reveal reveal--image">
          <ResponsiveImage image={images['contact-banner']} alt="Diamond Salon exterior in San Juan Capistrano" sizes="(max-width: 860px) 90vw, 55vw" objectPosition="center 54%" />
        </div>
        <div className="visit-section__copy reveal reveal--heading">
          <span className="editorial-index">07 / Visit</span>
          <h2>Your next look<br />starts <em>here.</em></h2>
          <p><MapPin /> {contact.address}</p>
          <div className="visit-section__links">
            <a className="button" href={contact.bookingUrl} target="_blank" rel="noopener noreferrer">Book an appointment <ArrowRight aria-hidden="true" /></a>
            <a className="text-link" href={contact.mapsUrl} target="_blank" rel="noopener noreferrer">Get directions to Diamond Salon <ArrowRight aria-hidden="true" /></a>
          </div>
        </div>
      </section>
    </>
  )
}
